import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Sparkles, Phone, Bot, FileText, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
interface ActivationGuideScreenProps {
  onComplete: () => void;
}
export function ActivationGuideScreen({
  onComplete
}: ActivationGuideScreenProps) {
  const {
    isLight
  } = useTheme();
  return <div className="h-full flex flex-col relative overflow-hidden" data-id="element-376">
      {/* Background effects */}
      <div className={`absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-vexu/10' : 'bg-vexu/5'}`} data-id="element-377" />
      <div className={`absolute bottom-0 left-0 w-48 h-48 rounded-full blur-3xl pointer-events-none ${isLight ? 'bg-blue-500/8' : 'bg-blue-500/5'}`} data-id="element-378" />

      {/* Confetti particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-379">
        {[...Array(16)].map((_, i) => <motion.div key={i} initial={{
        y: '110%',
        x: `${Math.random() * 100}%`,
        opacity: 0,
        scale: 0
      }} animate={{
        y: '-10%',
        opacity: [0, 0.8, 0],
        scale: [0, Math.random() * 0.8 + 0.4, 0]
      }} transition={{
        duration: 2.5 + Math.random() * 2,
        repeat: Infinity,
        delay: Math.random() * 3
      }} className={`absolute w-2 h-2 rounded-full ${i % 3 === 0 ? 'bg-vexu/50' : i % 3 === 1 ? isLight ? 'bg-blue-400/40' : 'bg-blue-400/30' : isLight ? 'bg-amber-400/40' : 'bg-amber-400/30'}`} data-id="element-380" />)}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 relative z-10" data-id="element-381">
        {/* Success icon */}
        <div className="relative mb-8" data-id="element-382">
          <motion.div animate={{
          scale: [1, 1.3, 1],
          opacity: [0.4, 0, 0.4]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full bg-vexu/20" data-id="element-383" />
          <motion.div animate={{
          scale: [1, 1.15, 1],
          rotate: [0, 5, -5, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute inset-0 rounded-full border-2 border-vexu/25" data-id="element-384" />
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 15,
          delay: 0.2
        }} className="relative w-24 h-24 rounded-full bg-vexu flex items-center justify-center shadow-[0_0_40px_rgba(0,200,83,0.35)]" data-id="element-385">
            <CheckCircle2 className="w-12 h-12 text-white" data-id="element-386" />
          </motion.div>
        </div>

        {/* Title */}
        <motion.h1 initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.3
      }} className={`text-2xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-387">
          You're Activated! 🎉
        </motion.h1>

        <motion.p initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.4
      }} className={`text-sm leading-relaxed mb-8 max-w-[280px] ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-388">
          Your AI personal assistant is ready to handle calls. Here's what you
          can expect:
        </motion.p>

        {/* Benefits */}
        <div className="w-full space-y-3 mb-6" data-id="element-389">
          {[{
          icon: Phone,
          title: 'Never miss a call',
          desc: "AI answers when you can't",
          delay: 0.5
        }, {
          icon: Bot,
          title: 'Natural conversations',
          desc: "Callers won't know it's AI",
          delay: 0.6
        }, {
          icon: FileText,
          title: 'Instant summaries',
          desc: 'Get transcripts & action items',
          delay: 0.7
        }].map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: item.delay,
          type: 'spring',
          stiffness: 300,
          damping: 25
        }} className={`flex items-center gap-3.5 p-3.5 rounded-2xl text-left ${isLight ? 'bg-white/80 border border-slate-100 shadow-sm' : 'bg-white/5 border border-white/10'}`} data-id="element-390">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-vexu/10' : 'bg-vexu/15'}`} data-id="element-391">
                <item.icon className={`w-5 h-5 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-392" />
              </div>
              <div data-id="element-393">
                <h3 className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-394">
                  {item.title}
                </h3>
                <p className={`text-xs mt-0.5 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-395">
                  {item.desc}
                </p>
              </div>
            </motion.div>)}
        </div>

        {/* Pro tip */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.9
      }} className={`flex items-center gap-2 px-4 py-2.5 rounded-full ${isLight ? 'bg-amber-50 border border-amber-100' : 'bg-amber-500/10 border border-amber-500/20'}`} data-id="element-396">
          <Sparkles className={`w-3.5 h-3.5 ${isLight ? 'text-amber-500' : 'text-amber-400'}`} data-id="element-397" />
          <span className={`text-xs font-medium ${isLight ? 'text-amber-700' : 'text-amber-300'}`} data-id="element-398">
            Enable call forwarding on the Home screen to get started
          </span>
        </motion.div>
      </div>

      {/* CTA Button */}
      <motion.div initial={{
      y: 30,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 1
    }} className="px-6 pb-12 relative z-10" data-id="element-399">
        <Button fullWidth onClick={onComplete} className="flex items-center justify-center gap-2" data-id="element-400">
          Start Exploring
          <ArrowRight className="w-4 h-4" data-id="element-401" />
        </Button>
      </motion.div>
    </div>;
}